import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DefaultDataServiceConfig,
  HttpUrlGenerator,
  Pluralizer,
} from '@ngrx/data';
import { Client, Service, IEP } from '@common/models';
import { DateTime, Duration } from 'luxon';
import { TransformingDataService } from '@common/entities/data-service';
import { Observable } from 'rxjs';
import { PlClientEntitiesDataService } from './clients';
import { isSet } from '../pl-util';

export interface JSONClient {
  uuid?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  external_id?: string;
  grade_display?: string;
  school?: string;
  location_uuid?: string;
  account_location_other?: string;
  services?: JSONService[];
  ieps?: JSONIEP[];
  created?: any;
}

export interface JSONService {
  uuid?: string;
  primary_disability?: string;
  service_type?: string;
  session_duration?: string;
  medicaid_eligible?: boolean;
  comments?: string;
  eligibility_date: string;
  service_frequency_per_interval?: number;
  service_interval?: string;
  therapy_type?: string;
}

export interface JSONIEP {
  uuid?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
  triennial_evaluation_date?: string;
  service?: JSONService;
}

function dateFromJSON(s: string): DateTime {
  return s ? DateTime.fromISO(s) : null;
}

function durationFromJSON(s: string): Duration {
  const duration = s ? Duration.fromISOTime(s) : null;
  if (duration && duration.toMillis() > 0) {
    return duration;
  }
  return null;
}

export class ConvertService {
  static fromJSON(service: JSONService): Service {
    return {
      ...service,
      eligibility_date: dateFromJSON(service.eligibility_date),
      session_duration: durationFromJSON(service.session_duration),
    };
  }

  static toJSON(service: Service): JSONService {
    return {
      ...service,
      eligibility_date: service.eligibility_date?.toISODate(),
      session_duration: service.session_duration?.as('minutes').toString(),
    };
  }
}

class ConvertIEP {
  static fromJSON(iep: JSONIEP): IEP {
    return {
      ...iep,
      start_date: dateFromJSON(iep.start_date),
      end_date: dateFromJSON(iep.end_date),
      triennial_evaluation_date: dateFromJSON(iep.triennial_evaluation_date),
      service: iep.service ? ConvertService.fromJSON(iep.service) : null,
    };
  }

  static toJSON(iep: IEP): JSONIEP {
    return {
      ...iep,
      start_date: iep.start_date?.toISODate(),
      end_date: iep.end_date?.toISODate(),
      triennial_evaluation_date: iep.triennial_evaluation_date?.toISODate(),
      service: iep.service ? ConvertService.toJSON(iep.service) : null,
    };
  }
}

export class ConvertClient {
  static fromJSON(client: JSONClient): Client {
    const { location_uuid: locationUuid, ...rest } = client;
    return {
      ...rest,
      account_location: locationUuid ?? null,
      date_of_birth: dateFromJSON(client.date_of_birth),
      created: dateFromJSON(client.created),
      services: client.services?.map(ConvertService.fromJSON) ?? [],
      ieps: client.ieps?.map(ConvertIEP.fromJSON) ?? [],
    };
  }

  static toJSON(client: Client): JSONClient {
    const { account_location: accountLocation, ...rest } = client;
    return {
      ...rest,
      location_uuid: accountLocation ?? null,
      date_of_birth: client.date_of_birth?.toISODate(),
      services: client.services?.map(ConvertService.toJSON) ?? [],
      ieps: client.ieps?.map(ConvertIEP.toJSON) ?? [],
    };
  }
}

export interface ClientProfile extends IEPProfile {
  uuid: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  age?: string;
  school?: string;
  gradeDisplay?: string;
  externalId?: string;
}

export interface IEPProfile {
  iepDueDate?: string;
  triennialDueDate?: string;
  therapyType: string;
  medicaidEligible?: string;
  primaryDisability?: string;
  serviceType: string;
  sessionDuration: string;
  serviceInterval: string;
  serviceFrequency: string;
  comments: string;
}

const entityParsers: Record<string, Function> = {
  ieps: ConvertIEP.fromJSON,
};

@Injectable()
export class PlClientDataService extends TransformingDataService<
  Client,
  JSONClient
> {
  pluralizer: Pluralizer;

  constructor(
    http: HttpClient,
    httpUrlGenerator: HttpUrlGenerator,
    pluralizer: Pluralizer,
    config?: DefaultDataServiceConfig
  ) {
    super(
      'Client',
      http,
      httpUrlGenerator,
      ConvertClient.fromJSON,
      ConvertClient.toJSON,
      config
    );
    this.pluralizer = pluralizer;
  }

  map(data: Client): ClientProfile {
    const mappedClient: ClientProfile = {
      uuid: '',
      firstName: '',
      lastName: '',
      medicaidEligible: '',
      iepDueDate: '',
      triennialDueDate: '',
      dateOfBirth: '',
      primaryDisability: '',
      age: '',
      school: '',
      gradeDisplay: '',
      externalId: '',
      serviceType: '',
      sessionDuration: '',
      serviceInterval: '',
      serviceFrequency: '',
      comments: '',
      therapyType: '',
    };

    mappedClient.uuid = data.uuid;
    // name is required
    mappedClient.firstName = data.first_name;
    mappedClient.lastName = data.last_name;

    mappedClient.dateOfBirth = data.date_of_birth
      ? data.date_of_birth.toLocaleString(DateTime.DATE_SHORT)
      : '';

    mappedClient.age = data.date_of_birth
      ? this.formatAge(data.date_of_birth)
      : '';

    mappedClient.school = data.school ? data.school : '';

    mappedClient.gradeDisplay = data.grade_display ? data.grade_display : '';

    mappedClient.externalId = data.external_id ? data.external_id : '';

    const iep = data.ieps?.[0];

    if (iep) {
      return {
        ...mappedClient,
        ...this.mapIEP(iep),
      };
    }

    return mappedClient;
  }

  mapIEP(iep: IEP): IEPProfile {
    const service = iep?.service;

    let medicaidEligible: IEPProfile['medicaidEligible'] = '';

    if (isSet(service?.medicaid_eligible)) {
      medicaidEligible = service.medicaid_eligible ? 'Yes' : 'No';
    }

    return {
      iepDueDate: iep?.end_date
        ? iep.end_date.toLocaleString(DateTime.DATE_SHORT)
        : '',
      triennialDueDate: iep?.triennial_evaluation_date
        ? iep.triennial_evaluation_date.toLocaleString(DateTime.DATE_SHORT)
        : '',
      medicaidEligible,
      primaryDisability: service?.primary_disability
        ? service.primary_disability
        : '',
      comments: service?.comments ? service.comments : '',
      serviceType: service?.service_type ? service.service_type : '',
      therapyType: service?.therapy_type ? service.therapy_type : '',
      serviceInterval: service?.service_interval
        ? service.service_interval
        : '',
      serviceFrequency: service?.service_frequency_per_interval
        ? this.maybePluralize(service.service_frequency_per_interval, 'time')
        : '',
      sessionDuration: service?.session_duration
        ? this.maybePluralize(service.session_duration.as('minutes'), 'minute')
        : '',
    };
  }

  formatAge(date: DateTime): string {
    return date
      ? Math.trunc(DateTime.now().diff(date, 'years').years).toString()
      : '';
  }

  maybePluralize(count: number, noun: string): string {
    return count === 1
      ? `${count} ${noun}`
      : `${count} ${this.pluralizer.pluralize(noun)}`;
  }

  bulk(data: any[]): Observable<any> {
    const url = `${this.entitiesUrl}bulk_create/`;
    return this.http.post(url, { client_services: data });
  }

  getEntityService<T>(entityName: string, clientId: string) {
    const url = `${this.entitiesUrl}${clientId}/${entityName}/`;
    return new PlClientEntitiesDataService<T>(this.http, url);
  }

  getEntityParser(entityName: string) {
    const defaultParser = (entity: any) => entity;
    return entityParsers[entityName] ?? defaultParser;
  }

  deleteObjective(clientUuid: string, objectiveUuid: string): Observable<any> {
    const url = `${this.entitiesUrl}${clientUuid}/objectives/${objectiveUuid}/`;
    return this.http.delete(url);
  }
}
